/**
 * Labor Tab Component
 * Features: CSV/PDF export, configurable row count, drill-down links, contextual empty state
 */

import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from "recharts";
import { Users } from "lucide-react";
import { 
  ChartCard, TableCard, CustomTooltip, StatusBadge, DataTable, 
  TableRow, TableCell, JobLinkCell, EmptyState, EMPTY_STATES 
} from "./shared";
import {
  CHART_COLORS, CHART_STYLE, formatCurrency, formatCompactCurrency,
  formatPercent, formatNumber, getEfficiencyColor, exportToCSV, DEFAULT_ROW_COUNT,
  generateAnalyticsPdfContent, generateTableHtml,
} from "../../lib/chartUtils";
import useExportDialog from "../../hooks/useExportDialog";

const LaborTab = ({ data }) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const { exportToPdf } = useExportDialog();

  if (!data?.length) {
    return (
      <EmptyState 
        icon={Users}
        {...EMPTY_STATES.labor}
      />
    );
  }

  const handleExportCSV = () => {
    exportToCSV(data, "labor-productivity",
      ["Job Name", "Status", "Hours", "Estimate", "Actual", "Budget Used %", "Cost/Hour"],
      {
        "Job Name": "job_name",
        "Status": "status",
        "Hours": "total_hours",
        "Estimate": "labor_estimate",
        "Actual": "labor_actual",
        "Budget Used %": "budget_used_pct",
        "Cost/Hour": "cost_per_hour",
      }
    );
  };

  const handleExportPDF = () => {
    const tableRows = data.slice(0, rowCount).map(job => [
      job.job_name,
      job.status,
      `${formatNumber(job.total_hours)}h`,
      formatCurrency(job.labor_estimate),
      formatCurrency(job.labor_actual),
      `<span class="${job.budget_used_pct <= 100 ? 'green' : 'red'}">${formatPercent(job.budget_used_pct)}</span>`,
      formatCurrency(job.cost_per_hour),
    ]);

    const tableHtml = generateTableHtml(
      ["Job", "Status", "Hours", "Estimate", "Actual", "Budget Used", "$/Hour"],
      tableRows,
      ["left", "left", "right", "right", "right", "right", "right"]
    );

    const totals = data.reduce((acc, job) => ({
      hours: acc.hours + job.total_hours,
      estimate: acc.estimate + job.labor_estimate,
      actual: acc.actual + job.labor_actual,
    }), { hours: 0, estimate: 0, actual: 0 });

    const summaryHtml = `
      <div style="display: flex; gap: 24px; margin-bottom: 16px;">
        <div><strong>Total Hours:</strong> ${formatNumber(totals.hours)}h</div>
        <div><strong>Total Estimate:</strong> ${formatCurrency(totals.estimate)}</div>
        <div><strong>Total Actual:</strong> ${formatCurrency(totals.actual)}</div>
      </div>
    `;

    const content = generateAnalyticsPdfContent("Labor Productivity Report", [
      { title: "Summary", content: summaryHtml },
      { title: "Labor Details by Job", content: tableHtml },
    ]);

    exportToPdf("Labor Productivity Report", content);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget Utilization Chart */}
        <ChartCard title="Labor Budget Utilization">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ top: 20, right: 40, left: 80, bottom: 20 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis type="number" domain={[0, 150]} tickFormatter={(v) => `${v}%`} tick={CHART_STYLE.axis} />
              <YAxis dataKey="job_name" type="category" tick={{ fontSize: 11 }} width={70} />
              <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
              <Bar dataKey="budget_used_pct" name="Budget Used %" radius={[0, 4, 4, 0]}>
                {data.slice(0, 8).map((entry, index) => (
                  <Cell key={entry.job_id} fill={getEfficiencyColor(entry.budget_used_pct)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Hours vs Cost Chart */}
        <ChartCard title="Hours & Cost by Job">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.slice(0, 8)} margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis dataKey="job_name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tickFormatter={(v) => `${v}h`} tick={CHART_STYLE.axis} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar yAxisId="left" dataKey="total_hours" name="Hours" fill={CHART_COLORS.info} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="labor_actual" name="Cost" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Labor Table */}
      <TableCard 
        title="Labor Productivity by Job"
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        rowCount={rowCount}
        onRowCountChange={setRowCount}
      >
        <DataTable headers={[
          { label: "Job" },
          { label: "Status" },
          { label: "Hours", align: "right" },
          { label: "Estimate", align: "right" },
          { label: "Actual", align: "right" },
          { label: "Budget Used", align: "right" },
          { label: "$/Hour", align: "right" },
        ]}>
          {data.slice(0, rowCount).map((job) => (
            <TableRow key={job.job_id} className="table-row hover:bg-muted/50">
              <JobLinkCell jobId={job.job_id}>
                {job.job_name}
              </JobLinkCell>
              <TableCell><StatusBadge status={job.status} /></TableCell>
              <TableCell className="text-right">{formatNumber(job.total_hours)}h</TableCell>
              <TableCell className="text-right">{formatCurrency(job.labor_estimate)}</TableCell>
              <TableCell className="text-right">{formatCurrency(job.labor_actual)}</TableCell>
              <TableCell className="text-right">
                <span className={`font-medium ${job.budget_used_pct <= 100 ? "text-green-600" : "text-red-600"}`}>
                  {formatPercent(job.budget_used_pct)}
                </span>
              </TableCell>
              <TableCell className="text-right">{formatCurrency(job.cost_per_hour)}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      </TableCard>
    </div>
  );
};

export default LaborTab;
